<script lang="ts" setup generic="T extends Record<string, unknown>">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { getPortalName, portalData, portalLoaded, portalTargets } from '../../helper/portal'

interface PortalTargetProps {
  id?: string

  /**
   * Alternative to id, generates a unique id to use mainly in local environments
   * Emits uniqueId event
   */
  unique?: boolean
  data?: T
}
const props = defineProps<PortalTargetProps>()

const emit = defineEmits(['uniqueId'])

function generateUniqueId(): string {
  return Math.random().toString(36).substring(2, 9)
}

const uniqueId = ref(generateUniqueId())
const portalId = computed(() => props.unique ? ((props.id ?? '') + uniqueId.value) : props.id!)

const idName = computed(() => getPortalName(portalId.value))

const hasEntries = computed(() => (portalTargets.get(portalId.value) ?? 0) > 0)

watch(() => portalId, () => {
  if (props.unique) {
    emit('uniqueId', portalId.value)
  }
}, { immediate: true })

watch(() => props.data, (data) => {
  if (data)
    portalData.set(portalId.value, data)
}, { immediate: true })

watch(() => portalId.value, (id, old) => {
  if (!portalLoaded.has(id)) {
    portalLoaded.add(id)
  }

  if (id !== old && old && portalLoaded.has(old)) {
    portalLoaded.delete(old)
  }
})

onMounted(() => {
  portalLoaded.add(portalId.value)
})

onUnmounted(() => {
  if (portalLoaded.has(portalId.value))
    portalLoaded.delete(portalId.value)
})
</script>

<template>
  <div :id="idName" class="d-contents" :class="{ 'portal-with-entries': hasEntries }">
    <slot v-if="!hasEntries" v-bind="data ? data : undefined" />
  </div>
</template>
